// Self-serve document generator.
//
// /generate           — landing: grid of doc types (NDA active, Grid
//                       types listed as "Coming soon" until legal
//                       provides templates).
// /generate/:type     — wizard: one question per step, completion
//                       screen links to the new draft contract.
//
// Flow state lives on the server (keyed by `flowId`), so this page
// just pushes answers and renders whatever the server returns next.
// Refreshing the page mid-wizard drops the local flowId and kicks the
// user back to step 1 — acceptable for v1; the Python bot has the
// same property.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { listGenerateTypes, startGenerateSession } from '../features/generate/generateApi';
import { useToast } from '../components/ToastProvider';
import Landing from '../features/generate/GenerateLanding';
import Launchpad from '../features/generate/GenerateLaunchpad';
import Wizard from '../features/generate/GenerateWizard';

export default function Generate() {
  const { type: typeParam, sessionId: sessionIdParam } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    listGenerateTypes()
      .then((data) => {
        setTypes(data?.types || []);
        setLoadingTypes(false);
      })
      .catch(() => setLoadingTypes(false));
  }, []);

  // Resolve the :type param to a registered spec. Redirect to landing
  // if the user hand-typed a bogus URL or navigated to a type that's
  // still "Coming soon."
  const selectedType = typeParam ? types.find((t) => t.key === typeParam) : null;
  const invalidSelection = typeParam && types.length > 0 && (!selectedType || !selectedType.available);
  useEffect(() => {
    if (invalidSelection) navigate('/intake', { replace: true });
  }, [invalidSelection, navigate]);

  // Create the session only when the user commits on the launchpad. Notes
  // pasted there ride along in router state so the wizard auto-runs prefill.
  const startIntake = async (notes) => {
    if (starting) return;
    setStarting(true);
    try {
      const body = await startGenerateSession(typeParam);
      const sid = body.sessionId || body.flowId;
      navigate(`/intake/${typeParam}/${sid}`, {
        state: notes && notes.trim() ? { prefillNotes: notes } : undefined,
      });
    } catch (e) {
      setStarting(false);
      showToast(e?.message || 'Could not start the intake', 'error');
    }
  };

  if (!typeParam) {
    return <Landing types={types} loading={loadingTypes} onStart={(k) => navigate(`/intake/${k}`)} />;
  }

  // Don't render until we know the selected type is valid (avoids a blank
  // flash that then hits the redirect above).
  if (!selectedType) {
    return (
      <div className="canvas-inner">
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ margin: '0 auto 10px' }} />
          <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
        </div>
      </div>
    );
  }

  // No session yet → the launchpad. Session present → the wizard.
  if (!sessionIdParam) {
    return (
      <Launchpad
        type={selectedType}
        starting={starting}
        onStart={startIntake}
        onCancel={() => navigate('/intake')}
      />
    );
  }

  return (
    <Wizard
      key={`${typeParam}:${sessionIdParam}`}
      typeKey={typeParam}
      sessionIdFromUrl={sessionIdParam}
      typeMeta={{ displayName: selectedType.displayName }}
      onRestart={() => navigate('/intake')}
    />
  );
}
